import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  CreatePurchaseOrderPayload,
  PoTemplate,
  PoTemplateLayout,
  PoTemplateVersion,
  PurchaseOrderDetail,
  PurchaseOrderFilter,
  PurchaseOrderLineInput,
  PurchaseOrderPrefill,
  PurchaseOrderSummary,
  UpdatePurchaseOrderPayload,
} from '@/types/purchaseOrders'

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), {
      status: res.status,
    })
  }
  return res.json() as Promise<T>
}

const PO = '/inventory/purchase-orders'

export type PurchaseOrderListParams = {
  status?: PurchaseOrderFilter
  supplier?: string
  materialId?: string
  q?: string
}

/** Purchase orders (z8nrz7f7dz). Mirrors cgt-backend src/inventory/purchase-orders. */
export const purchaseOrdersApi = {
  list: (tenantId: string, params: PurchaseOrderListParams = {}) => {
    const qs = new URLSearchParams()
    // `all` is the absence of a filter.
    if (params.status && params.status !== 'all') qs.set('status', params.status)
    if (params.supplier) qs.set('supplier', params.supplier)
    if (params.materialId) qs.set('materialId', params.materialId)
    if (params.q) qs.set('q', params.q)
    const query = qs.toString()
    return request<{ items: PurchaseOrderSummary[] }>(`${PO}${query ? `?${query}` : ''}`, tenantId)
  },

  get: (tenantId: string, id: string) => request<PurchaseOrderDetail>(`${PO}/${id}`, tenantId),

  /** The order as it stood at an earlier revision. */
  getRevision: (tenantId: string, id: string, revision: number) =>
    request<PurchaseOrderDetail>(`${PO}/${id}/revisions/${revision}`, tenantId),

  /** Supplier and line proposed for ordering one material. */
  prefill: (tenantId: string, materialId: string) =>
    request<PurchaseOrderPrefill>(`${PO}/prefill?materialId=${encodeURIComponent(materialId)}`, tenantId),

  create: (tenantId: string, payload: CreatePurchaseOrderPayload) =>
    request<PurchaseOrderDetail>(PO, tenantId, { method: 'POST', body: JSON.stringify(payload) }),

  update: (tenantId: string, id: string, payload: UpdatePurchaseOrderPayload) =>
    request<PurchaseOrderDetail>(`${PO}/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /** Append one line. DRAFT only. */
  addLine: (tenantId: string, id: string, line: PurchaseOrderLineInput) =>
    request<PurchaseOrderDetail>(`${PO}/${id}/lines`, tenantId, {
      method: 'POST',
      body: JSON.stringify(line),
    }),

  send: (tenantId: string, id: string) =>
    request<PurchaseOrderDetail>(`${PO}/${id}/send`, tenantId, { method: 'POST' }),

  receive: (tenantId: string, id: string) =>
    request<PurchaseOrderDetail>(`${PO}/${id}/receive`, tenantId, { method: 'POST' }),

  cancel: (tenantId: string, id: string, reason: string) =>
    request<PurchaseOrderDetail>(`${PO}/${id}/cancel`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ─── Template (per tenant, versioned) ───────────────────────────────────

  getTemplate: (tenantId: string) => request<PoTemplate>(`${PO}/template`, tenantId),

  // The server wraps the list as { items } (newest first); a bare array is accepted too.
  listTemplateVersions: async (tenantId: string): Promise<PoTemplateVersion[]> => {
    const res = await request<{ items: PoTemplateVersion[] } | PoTemplateVersion[]>(`${PO}/template/versions`, tenantId)
    return Array.isArray(res) ? res : (res?.items ?? [])
  },

  getTemplateVersion: (tenantId: string, version: number) =>
    request<PoTemplate>(`${PO}/template/versions/${version}`, tenantId),

  /** Saves a NEW version; earlier ones stay, and sent orders keep their snapshot. */
  saveTemplate: (tenantId: string, layout: PoTemplateLayout, note?: string) =>
    request<PoTemplate>(`${PO}/template`, tenantId, {
      method: 'PUT',
      body: JSON.stringify({ layout, note: note || undefined }),
    }),

  /** A new version equal to the built-in default. */
  resetTemplate: (tenantId: string) =>
    request<PoTemplate>(`${PO}/template/reset`, tenantId, { method: 'POST' }),

  /**
   * Upload a logo (png/jpeg/svg, ≤ 1 MB).
   *
   * Plain `fetch`, not `authFetch`: both `authHeaders` and `authFetch` put a
   * JSON content type on every request, and a multipart body needs the one
   * the browser writes itself, boundary included. The bearer token and tenant
   * are copied across; the cost is no silent token renewal on this one call.
   */
  uploadLogo: async (tenantId: string, file: File): Promise<{ logoAssetKey: string }> => {
    const body = new FormData()
    body.append('file', file)
    const headers = Object.fromEntries(
      Object.entries(authHeaders({ 'X-Tenant-Id': tenantId }) as Record<string, string>).filter(
        ([k]) => k.toLowerCase() !== 'content-type',
      ),
    )
    const res = await fetch(`${BASE}${PO}/template/logo`, { method: 'POST', headers, body })
    handleUnauthorized(res.status)
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as { message?: string | string[] } | null
      const message = Array.isArray(err?.message) ? err.message.join('; ') : err?.message
      throw Object.assign(new Error(message || `Upload failed: ${res.status}`), { status: res.status })
    }
    return res.json() as Promise<{ logoAssetKey: string }>
  },

  /**
   * The logo's bytes. Behind auth and the tenant header, so an <img src> to the
   * route would go out without the bearer token — callers turn this Blob into
   * an object URL instead (usePoLogo).
   */
  getLogo: async (tenantId: string, key: string): Promise<Blob> => {
    const res = await authFetch(`${BASE}${PO}/template/logo/${encodeURIComponent(key)}`, {
      headers: authHeaders({ 'X-Tenant-Id': tenantId }),
    })
    handleUnauthorized(res.status)
    if (!res.ok) throw Object.assign(new Error(`Logo request failed: ${res.status}`), { status: res.status })
    return res.blob()
  },
}
